import React from "react";
import CommonTextField from "../commonTextField/CommonTextField";
import FlexibleTextarea from "../textArea/FlexibleTextarea";
import { useSelector } from "react-redux";
import { api1 } from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

// ✅ Yup validation schema
const schema = yup.object().shape({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  mentorshipGoals: yup
    .string()
    .required("Please specify your mentorship goals"),
  careerGoals: yup
    .string()
    .required("Please specify your career goals"),
  currentChallenges: yup
    .string()
    .required("Please describe your current challenges"),
  aboutYou: yup.string().nullable(),
  agreeTerms: yup
    .boolean()
    .oneOf([true], "You must agree to the terms to continue"),
});

const MentoringInputForm = ({ setIsMenteeApplicationOpen }) => {
  const { id: userId, firstName, lastName } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: firstName || "",
      lastName: lastName || "",
      mentorshipGoals: "",
      careerGoals: "",
      currentChallenges: "",
      aboutYou: "",
      agreeTerms: false,
    },
  });

  const onSubmit = async (data) => {
    try {
      await api1.post(`${API_ENDPOINTS.MENTORING_APPLICATION}/${userId}`, data);
    } catch (err) {
      console.log(err);
    } finally {
      setIsMenteeApplicationOpen(false);
    }
  };

  const handleCancel = () => {
    setIsMenteeApplicationOpen(false);
    navigate("/dashboard/mentors");
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="max-w-4xl mx-auto rounded-2xl overflow-hidden bg-white"
    >
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-8 py-6">
        <h2 className="text-2xl font-bold text-white">
          Mentoring Session Booking Form
        </h2>
        <p className="text-blue-100 mt-2">
          Complete this to prepare and get the most from your mentoring session
        </p>
      </div>

      <div className="p-5 space-y-5">
        {/* Personal Information */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
            Personal Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <CommonTextField
              name="firstName"
              label="First Name"
              register={register}
              required
              errors={errors}
              placeholder="Enter your first name"
              defaultValue={firstName}
            />
            <CommonTextField
              name="lastName"
              label="Last Name"
              register={register}
              required
              errors={errors}
              placeholder="Enter your last name"
              defaultValue={lastName}
            />
          </div>
        </div>

        {/* Goals & Background */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
            Goals & Background
          </h3>
          <div className="space-y-6">
            <FlexibleTextarea
              name="mentorshipGoals"
              register={register}
              required
              label="What specific outcomes do you hope to achieve from this mentorship?"
              errors={errors}
              placeholder="E.g., Improve technical skills, navigate career change, etc."
            />
            <FlexibleTextarea
              name="careerGoals"
              register={register}
              required
              label="What are your short and long-term career goals?"
              errors={errors}
              placeholder="Describe where you want to be in 1-2 years and 5+ years"
            />
            <FlexibleTextarea
              name="currentChallenges"
              register={register}
              required
              label="What challenges are you currently facing in your professional journey?"
              errors={errors}
              placeholder="Describe obstacles or areas where you feel stuck"
            />
            <FlexibleTextarea
              name="aboutYou"
              register={register}
              label="What else should the mentor know about you?"
              errors={errors}
              placeholder="Include relevant experience, interests, and what makes you unique"
            />
          </div>
        </div>

        {/* Agreement Section */}
        <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
            <svg
              className="w-5 h-5 mr-2 text-blue-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            Mentee Agreement
          </h3>

          <p className="text-sm text-gray-700 mb-4">
            By submitting this application, you agree to fulfill the following responsibilities:
          </p>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm text-gray-700">
            <li>✔ Set clear, achievable goals for your mentorship</li>
            <li>✔ Arrive on time and prepared for all sessions</li>
            <li>✔ Actively implement feedback between meetings</li>
            <li>✔ Provide 24+ hours notice for any schedule changes</li>
            <li>✔ Maintain professional conduct and confidentiality</li>
            <li>✔ Take ownership of your growth and development</li>
          </ul>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="agreeTerms"
              {...register("agreeTerms")}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="agreeTerms" className="ml-2 text-sm text-gray-700">
              I understand and agree to these responsibilities
            </label>
          </div>
          {errors.agreeTerms && (
            <p className="mt-1 text-sm text-red-600">
              {errors.agreeTerms.message}
            </p>
          )}
        </div>
      </div>

      <div className="px-8 py-6 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-end gap-4">
        <button
          type="button"
          className="w-full sm:w-auto px-8 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="w-full sm:w-auto px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
        >
          Book Session
        </button>
      </div>
    </form>
  );
};

export default MentoringInputForm;
